import { Plan } from '../Plans'

/**
 * Visibilidade dos planos no link de venda direta.
 *
 * O GET /planos é o mesmo para ERP, app, site, link de vendas e recarga fácil,
 * e traz um `hidden_*` por canal: uma marca pode ter mais de um portfólio e
 * nem todo plano é vendido em todo canal. O link de vendas é o canal
 * "solução vendas", então só considera `hidden_solucao_vendas`.
 *
 * Só o booleano `true` oculta. A ausência do campo (API sem a versão que o
 * expõe) ou qualquer outro valor mantém o plano visível de propósito: tratar
 * `undefined` como oculto tiraria todos os planos de todas as marcas de uma vez.
 */
export function isPlanVisibleInSalesLink(plan: Plan): boolean {
  return plan.hidden_solucao_vendas !== true
}

/** Planos que o link de vendas exibe, na ordem em que a API devolveu. */
export function filterSalesLinkPlans(plans: Plan[]): Plan[] {
  return plans.filter(isPlanVisibleInSalesLink)
}

/**
 * O que fazer com o plano do carrinho recuperado no checkout.
 *
 *  - `aguardar`: a lista de planos ainda não chegou, então não dá para dizer
 *    que o plano sumiu;
 *  - `aplicar`: o plano está na lista. Na compra de chip avulso não há plano
 *    para procurar, e o comportamento original (aplicar o resultado da busca)
 *    é preservado;
 *  - `reiniciar`: a lista carregou e o plano não está nela — foi ocultado do
 *    link de vendas depois de escolhido (ex.: F5 no pagamento). Seguir com ele
 *    exibiria um plano que o canal não vende, e trocar por outro em silêncio
 *    mostraria um preço diferente do que a compra cobra.
 */
export type CartPlanResolution =
  | { acao: 'aguardar' }
  | { acao: 'aplicar'; plano: Plan | undefined }
  | { acao: 'reiniciar' }

export interface ResolveCartPlanInput {
  plans: Plan[]
  idPlano: string
  loadingPlan: boolean
  chipOnly: boolean
}

export function resolveCartPlan({
  plans,
  idPlano,
  loadingPlan,
  chipOnly,
}: ResolveCartPlanInput): CartPlanResolution {
  if (loadingPlan) return { acao: 'aguardar' }

  const plano = plans.find(plan => plan.id === parseInt(idPlano))
  if (plano || chipOnly) return { acao: 'aplicar', plano }

  return { acao: 'reiniciar' }
}
